import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/logo.png";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { supabase } from "../lib/supabase";

const emptyForm = {
  fullName: "",
  email: "",
  boardPosition: "",
  note: "",
  website: "",
};

export function RequestAccessPage() {
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const handleTurnstileToken = useCallback((token) => setTurnstileToken(token), []);
  const handleTurnstileError = useCallback((message) => setError(message), []);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    if (!turnstileToken) {
      setError("Complete the security verification before submitting your request.");
      return;
    }
    setSubmitting(true);

    const { data, error: requestError } = await supabase.functions.invoke("request-board-access", {
      body: {
        fullName: form.fullName,
        email: form.email,
        boardPosition: form.boardPosition,
        note: form.note || null,
        website: form.website || null,
        turnstileToken,
      },
    });

    setSubmitting(false);
    if (requestError || data?.error) {
      setError(data?.error || requestError?.message || "We could not submit the request. Please try again.");
      setTurnstileToken("");
      setTurnstileResetKey((current) => current + 1);
      return;
    }

    setForm(emptyForm);
    setSubmitted(true);
  }

  return (
    <div className="screen centered access-request-screen">
      <section className="card access-request-card" aria-labelledby="request-access-title">
        <div className="access-request-heading">
          <img src={logo} width="150" height="100" alt="Coach Homes III COA" />
          <div>
            <span className="eyebrow">Newsletter portal</span>
            <h1 id="request-access-title">Request Board Access</h1>
            <p>This form is for current elected board members who do not already have a Resident Center account. Every request requires administrator approval.</p>
          </div>
        </div>

        {submitted ? (
          <section className="access-request-success" aria-live="polite">
            <h2>Request received</h2>
            <p>If the information matches an eligible board member, an administrator will review the request and send account instructions by email.</p>
            <p>For privacy, this page does not confirm whether an email address already has an account or a pending request.</p>
            <Link className="button" to="/login">Return to Sign In</Link>
          </section>
        ) : (
          <form className="access-request-form" onSubmit={handleSubmit}>
            {error ? <div className="error-block" role="alert">{error}</div> : null}
            <label>
              Full name
              <input required minLength="2" maxLength="100" autoComplete="name" value={form.fullName} onChange={(event) => update("fullName", event.target.value)} />
            </label>
            <label>
              Email address
              <input required maxLength="254" type="email" autoComplete="email" value={form.email} onChange={(event) => update("email", event.target.value)} />
            </label>
            <label>
              Elected board position
              <input required minLength="2" maxLength="100" value={form.boardPosition} onChange={(event) => update("boardPosition", event.target.value)} placeholder="Example: Secretary or Director" />
            </label>
            <label>
              Note for the administrator <span className="optional-label">Optional</span>
              <textarea maxLength="1000" rows="4" value={form.note} onChange={(event) => update("note", event.target.value)} placeholder="Add anything that will help verify your request." />
            </label>
            <label className="request-honeypot" aria-hidden="true">
              Website
              <input tabIndex="-1" autoComplete="off" value={form.website} onChange={(event) => update("website", event.target.value)} />
            </label>
            <div className="access-request-notice">
              Already have a resident login? Sign in and request a board role from the Resident Center instead. Submitting this form does not create an account or grant access.
            </div>
            <TurnstileWidget action="request_board_access" siteKey={turnstileSiteKey} onToken={handleTurnstileToken} onError={handleTurnstileError} resetKey={turnstileResetKey} />
            <button type="submit" disabled={submitting || !turnstileToken}>{submitting ? "Submitting request…" : "Submit Access Request"}</button>
            <Link className="access-request-back" to="/login">Already have access? Return to sign in</Link>
          </form>
        )}
      </section>
    </div>
  );
}
